/// <amd-module name="Test/TestPlansHubRefresh/SuitesMenu/SuitesMenuService" />
import * as React from "react";
import { PointsActions } from "Test/TestPlansHubRefresh/Pivots/ExecutePivot/Redux/PointsActions";
import { ISuitesMenu } from "Test/TestPlansHubRefresh/SuitesMenu/SuitesMenu.Types";
import * as Constants from "Test/TestPlansHubRefresh/SuitesMenu/SuitesMenuConstants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ISuitesMenuService extends IVssService {
    assignConfiguration(props: ISuitesMenu): void;
    assignTestersToSuite(props: ISuitesMenu): void;
    openTestSuite(props: ISuitesMenu): void;
    createNewReqSuite(props: ISuitesMenu): void;
    createStaticSuite(props: ISuitesMenu): void;
}

export function openDeleteTestSuiteDialog(params: ISuitesMenu): void {
    if (params.suite.id) {
        params.dispatch(
            SuitesActions.openDeleteTestSuiteDialog(
                params.planId,
                params.suite.id,
                params.rootSuiteId,
                params.suite.title,
                params.suite.type,
                params.selection,
                params.dispatch
            )
        );
    }
}

export function renameTestSuite(params: ISuitesMenu): void {
    if (params.suite.id) {
        params.dispatch(SuitesActions.editTestSuiteName(params.suite.id, params.suite.title));
    }
}

export function openExportHTMLDialog(params: ISuitesMenu): void {
    if (params.suite) {
        params.dispatch(
            SuitesActions.openExportHTMLDialog(
                params.planId,
                params.suite.id,
                params.suite.pointCount,
                params.rootSuiteId,
                params.suite.totalPointCount
            )
        );
    }
}

export function openRequirementWorkitem(params: ISuitesMenu): void {
    if (params.suite && params.suite.requirementId) {
        params.dispatch(SuitesActions.openRequirementWorkItemForm(params.suite.requirementId))
    }
}

class SuitesMenuService extends VssService implements ISuitesMenuService {
    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
    }

    public assignConfiguration(props: ISuitesMenu): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        const dialogType = Constants.AssignConfigToSuiteDialog;
        layoutManager.renderCallout(onDismiss => {
            const properties: ILegacyComponentProperties = {
                modules: ["TestManagement/Scripts/Shared/AssignConfigDialogProxy"],
                wrappedType: "assign-config-dialog-proxy",
                wrappedProps: {
                    dialogType,
                    ...props,
                    onSuccess: () => {
                        props.dispatch(PointsActions.unloadPreviousTestPoints());
                        props.dispatch(PointsActions.loadTestPointsForSelectedSuite(props.planId, props.suite.id));
                        props.dispatch(SuitesActions.getSuitePointCount(props.planId, [], [], [], [], undefined));
                        props.dispatch(SuitesActions.showToastNotifications(Resources.ConfigurationUpdated, 3000));
                    }
                },
                loadingComponent: () => {
                    return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
                }
            };

            return React.createElement(LegacyComponent, properties);
        });
    }

    public assignTestersToSuite(params: ISuitesMenu): void {
        if (params.suite) {
            params.dispatch(SuitesActions.assignTestersToSuite(params.planId, params.suite));
        }
    }

    public openTestSuite(params: ISuitesMenu): void {
        if (params.suite.id) {
            params.dispatch(SuitesActions.openTestSuiteWitForm(params.planId, params.suite.id, params.suite.title, params.rootSuiteId));
        }
    }

    public createNewReqSuite(props: ISuitesMenu): void {
        if (props.suite.id) {
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss => {
                const properties: ILegacyComponentProperties = {
                    modules: ["TestManagement/Scripts/Shared/CreateNewRBSProxy"],
                    wrappedType: "create-rbs-dialog-proxy",
                    wrappedProps: {
                        areaPath: props.planAreaPath,
                        parentSuiteId: props.suite.id,
                        onSuccess: (requirementIds: number[]) => {
                            props.dispatch(
                                SuitesActions.createNewReqSuite(
                                    props.planId,
                                    props.suite.id,
                                    props.suite.title,
                                    TestSuiteType.RequirementTestSuite,
                                    props.rootSuiteId,
                                    requirementIds,
                                    props.selection
                                )
                            );
                        }
                    },
                    loadingComponent: () => {
                        return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
                    }
                };

                return React.createElement(LegacyComponent, properties);
            });
        }
    }

    public createNewQuerySuite(props: ISuitesMenu): void {
        if (props.suite.id) {
            props.dispatch(
                SuitesActions.openCreateEditSuiteQueryForm(
                    props.planAreaPath,
                    true,
                    "",
                    props.planId,
                    props.suite.id,
                    props.suite.title,
                    props.rootSuiteId,
                    props.selection
                )
            );
        }
    }

    public createStaticSuite(params: ISuitesMenu): void {
        if (params.suite.id) {
            params.dispatch(
                SuitesActions.createStaticSuite(params.planId, params.suite.id, params.suite.title, TestSuiteType.StaticTestSuite, params.rootSuiteId)
            );
        }
    }
}

Services.add("ISuitesMenuService", { serviceFactory: SuitesMenuService });
